import { createContext, useContext, useCallback, useMemo } from 'react';
import { toast as sonnerToast } from 'sonner';
import { Toaster } from '@/components/ui';
import { TOAST_TYPES, ToastType } from '@/constants/common.constants';

interface ToastOptions {
  title?: string;
  duration?: number;
  autohide?: boolean;
  type?: ToastType;
}

interface ToastContextType {
  addToast: (
    content: string | React.ReactNode,
    options?: ToastOptions,
  ) => string | number;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const addToast = useCallback(
    (content: string | React.ReactNode, options: ToastOptions = {}) => {
      const type = options.type || TOAST_TYPES.SUCCESS;
      const duration = options.duration || 10000;

      // Map custom toast types to Sonner types
      let sonnerType: 'success' | 'error' | 'warning' | 'info' = 'success';
      if (type === TOAST_TYPES.ERROR) {
        sonnerType = 'error';
      } else if (type === TOAST_TYPES.WARNING) {
        sonnerType = 'warning';
      } else if (type === TOAST_TYPES.INFO || type === TOAST_TYPES.DOWNLOAD) {
        sonnerType = 'info';
      }

      // Sonner uses first param as title, description as option
      // If title is provided, use it as title and content as description
      // Otherwise, use content as title
      const title = options.title || String(content);
      const description = options.title ? String(content) : undefined;

      const toastId = sonnerToast[sonnerType](title, {
        duration: options.autohide !== false ? duration : Infinity,
        ...(description && { description }),
      });

      return toastId;
    },
    [],
  );

  const contextValue = useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
};
